import { useEffect, useState, useRef, useCallback } from 'react';
import { NotificationBanner } from '@hm-group/bads-react';
import type { BannerConfig } from '../../models/maintenance';
import styles from './MaintenanceBanner.module.css';

const DISMISSAL_KEY = 'notificationBanner_dismissed';
const DISMISSAL_HOURS = 24;

function isDismissed(): boolean {
  const dismissed = localStorage.getItem(DISMISSAL_KEY);
  if (!dismissed) return false;
  const hoursSince = (Date.now() - new Date(dismissed).getTime()) / (1000 * 60 * 60);
  return hoursSince < DISMISSAL_HOURS;
}

function saveDismissal(): void {
  localStorage.setItem(DISMISSAL_KEY, new Date().toISOString());
}

function formatCountdown(diffMs: number): string {
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  if (hours > 0) return `in ${hours}h ${minutes}m`;
  if (minutes > 0) return `in ${minutes}m ${seconds}s`;
  return `in ${seconds}s`;
}

interface MaintenanceBannerProps {
  config: BannerConfig;
  onCountdownComplete?: () => void;
}

export function MaintenanceBanner({ config, onCountdownComplete }: Readonly<MaintenanceBannerProps>) {
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState('');
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Update visibility when config changes
  useEffect(() => {
    if (config.dismissible && isDismissed()) {
      setIsVisible(false);
    } else {
      setIsVisible(config.show);
    }
  }, [config]);

  // Manage countdown timer
  useEffect(() => {
    stopCountdown();

    if (!config.showCountdown || !config.countdownTarget) {
      setCountdown('');
      return;
    }

    const target = config.countdownTarget;
    const tick = () => {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) {
        setCountdown('Starting now...');
        stopCountdown();
        onCountdownComplete?.();
        return;
      }
      setCountdown(formatCountdown(diff));
    };

    tick();
    countdownRef.current = setInterval(tick, 1000);

    return stopCountdown;
  }, [config.showCountdown, config.countdownTarget, stopCountdown, onCountdownComplete]);

  const handleDismiss = () => {
    if (config.dismissible) {
      setIsVisible(false);
      saveDismissal();
    }
  };

  if (!isVisible) return null;

  const countdownText = config.message ? `${config.message} — ${countdown}` : countdown;
  const subtitle = config.showCountdown && countdown
    ? countdownText
    : config.message ?? '';

  return (
    <div className={styles.container}>
      <NotificationBanner
        type={config.type === 'information' ? 'information' : config.type}
        title={config.title}
        subtitle={subtitle}
        isVisible
        showCloseIcon={config.dismissible}
        noMargin
        onClose={config.dismissible ? handleDismiss : undefined}
      />
    </div>
  );
}
